import { useState, useEffect, useCallback, useMemo } from "react";
import { TrendingUp, TrendingDown, Gift, PiggyBank } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import StatCard from "@/components/dashboard/StatCard";
import MoneyFlowChart from "@/components/dashboard/MoneyFlowChart";
import GiftCardChart from "@/components/dashboard/GiftCardChart";
import DateRangeFilter, {
  getDefaultRange,
  toISO,
  type DateRange,
} from "@/components/common/DateRangeFilter";
import { useAuth } from "@/context/AuthContext";
import {
  getWalletTrend,
  getGiftCardPurchases,
  type WalletTransaction,
  type GiftCardPurchase,
} from "@/utils/dashboard-api";

function formatINR(paise: number): string {
  const rupees = paise / 100;
  return "₹" + rupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function HomePage() {
  const { userId, token } = useAuth();
  const [range, setRange] = useState<DateRange>(getDefaultRange);

  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [purchases, setPurchases] = useState<GiftCardPurchase[]>([]);
  const [walletLoading, setWalletLoading] = useState(true);
  const [gcLoading, setGcLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    const from = toISO(range.from);
    const to = toISO(range.to);
    setWalletLoading(true);
    setGcLoading(true);
    setError("");

    const walletPromise = getWalletTrend(userId, from, to, token)
      .then((data) => setTransactions(data))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load wallet data"))
      .finally(() => setWalletLoading(false));

    const gcPromise = getGiftCardPurchases(userId, from, to, token)
      .then((data) => setPurchases(data))
      .catch((err) => setError((prev) => prev || (err instanceof Error ? err.message : "Failed to load gift card data")))
      .finally(() => setGcLoading(false));

    await Promise.all([walletPromise, gcPromise]);
  }, [userId, token, range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = useMemo(() => {
    const totalCredits = transactions.filter((t) => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
    const totalDebits = transactions.filter((t) => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const gcFaceValue = purchases.reduce((sum, p) => sum + p.amount, 0);
    const gcPaid = purchases.reduce((sum, p) => sum + p.discounted_amount, 0);
    const gcSavings = gcFaceValue - gcPaid;
    return { totalCredits, totalDebits, gcFaceValue, gcSavings, txnCount: transactions.length, gcCount: purchases.length };
  }, [transactions, purchases]);

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of your wallet activity and gift card purchases.
          </p>
        </div>
        <DateRangeFilter value={range} onChange={setRange} />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Credits"
          value={formatINR(stats.totalCredits)}
          subtitle={`${stats.txnCount} transactions`}
          icon={<TrendingUp className="h-[18px] w-[18px]" />}
          loading={walletLoading}
          valueColor="#16a34a"
        />
        <StatCard
          title="Total Debits"
          value={formatINR(stats.totalDebits)}
          subtitle="All outgoing payments"
          icon={<TrendingDown className="h-[18px] w-[18px]" />}
          loading={walletLoading}
          valueColor="#dc2626"
        />
        <StatCard
          title="Gift Cards"
          value={formatINR(stats.gcFaceValue)}
          subtitle={`${stats.gcCount} orders`}
          icon={<Gift className="h-[18px] w-[18px]" />}
          loading={gcLoading}
        />
        <StatCard
          title="Total Savings"
          value={formatINR(stats.gcSavings)}
          subtitle="Discount on gift cards"
          icon={<PiggyBank className="h-[18px] w-[18px]" />}
          loading={gcLoading}
          valueColor="#16a34a"
        />
      </div>

      {/* Charts */}
      <MoneyFlowChart transactions={transactions} loading={walletLoading} />
      <GiftCardChart purchases={purchases} loading={gcLoading} />
    </div>
  );
}
